import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { ICampaignAnalytics } from '@interfaces/campaign-analytics.interface';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TPlatform } from '@shared/components/social-badge-list/social-badge-list.interface';
import { SocialBadgeComponent } from '@shared/components/social-badge/social-badge.component';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { map, startWith } from 'rxjs';
import {
  IProfileMultiSelectState,
  ProfileMultiSelectFilterService,
} from '../profile-general-filters/profile-multi-select-filter.service';
import { CampaignDetailsPlatformFeaturesService } from '../profile-platform-feature-filter/profile-platform-feature-filter.service';

@Component({
  imports: [
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    NgxSkeletonLoaderModule,
    SocialBadgeComponent,
    TranslateModule,
  ],
  selector: 'app-profile-platform-feature-select',
  templateUrl: './profile-platform-feature-select.component.html',
})
export class ProfilePlatformFeatureSelectComponent {
  private readonly multiSelectFilterService = inject(ProfileMultiSelectFilterService);
  private readonly translate = inject(TranslateService);

  public readonly campaignAnalytics = input.required<ICampaignAnalytics | null>();
  public readonly initialSelectedPlatformFeatures = input<Set<TPlatform>>(new Set());
  public readonly loading = input<boolean>(true);
  // mat-select's `panelWidth`: 'auto' matches the trigger, null sizes the panel to its options.
  public readonly panelWidth = input<string | number | null>('auto');

  public readonly platformFeaturesChanged = output<Set<TPlatform>>();
  public readonly panelClosed = output<void>();
  public readonly selectionNormalized = output<Set<TPlatform>>();
  public readonly selectionState = signal<IProfileMultiSelectState<TPlatform>>(
    this.multiSelectFilterService.createUnfilteredSelectionState<TPlatform>(),
  );
  private isPanelOpen = false;
  private panelSelectionSnapshot: string | null = null;
  public readonly languageChange = toSignal(
    this.translate.onLangChange.pipe(
      map((event) => event.lang),
      startWith(this.translate.currentLang),
    ),
    { initialValue: this.translate.currentLang },
  );

  public readonly platformFeatures = computed(() => {
    const campaignAnalytics = this.campaignAnalytics();

    if (!campaignAnalytics) {
      return [];
    }

    return Array.from(this.campaignDetailsPlatformFeaturesService.getCampaignPlatformFeatures(campaignAnalytics)).sort(
      (a, b) => a.localeCompare(b),
    );
  });

  public readonly totalPlatformCount = computed(() => this.platformFeatures().length);

  public readonly emptyLabel = computed(() => {
    this.languageChange();

    const totalPlatformCount = this.totalPlatformCount();

    if (totalPlatformCount === 0) {
      return this.translate.instant('pages.campaignDetails.filter.allPlatforms');
    }

    return `${this.translate.instant('pages.campaignDetails.filter.allPlatforms')} (${totalPlatformCount})`;
  });

  public readonly triggerLabel = computed(() => {
    this.languageChange();

    const orderedSelectedPlatformFeatures = this.platformFeatures().filter((platformFeature) =>
      this.selectionState().uiSelectedValues.includes(platformFeature),
    );

    return this.multiSelectFilterService.buildSelectionSummary(
      this.translate.instant(`shared.platformFeatures.${orderedSelectedPlatformFeatures[0]}`),
      orderedSelectedPlatformFeatures.length,
      (this.translate.instant('shared.more') as string).toLowerCase(),
    );
  });

  private readonly campaignDetailsPlatformFeaturesService = inject(CampaignDetailsPlatformFeaturesService);

  constructor() {
    effect(() => {
      if (this.loading()) {
        this.isPanelOpen = false;
      }

      const nextState: IProfileMultiSelectState<TPlatform> = this.multiSelectFilterService.syncSelectionState(
        this.platformFeatures(),
        this.initialSelectedPlatformFeatures(),
        this.selectionState(),
      );

      if (!this.selectionStatesAreEqual(this.selectionState(), nextState)) {
        this.selectionState.set(nextState);

        // The available platform set (campaignAnalytics) or the parent's requested selection
        // (initialSelectedPlatformFeatures) can change in a way that silently drops a previously
        // selected, now-unavailable platform from the displayed selection (see
        // ProfileMultiSelectFilterService.syncSelectionState). Without this, the parent's own
        // filter state would keep referencing a platform the UI no longer shows as selected -
        // inform it the same way every other selection change does, so it stays in sync.
        const normalizedPlatformFeatures = this.emitPlatformFeatures();

        if (!this.isPanelOpen) {
          this.selectionNormalized.emit(normalizedPlatformFeatures);
        }
      }
    });
  }

  public onSelectionChange(event: MatSelectChange): void {
    const nextSelectedPlatformFeatures: TPlatform[] = event.value ?? [];
    const nextState: IProfileMultiSelectState<TPlatform> =
      nextSelectedPlatformFeatures.length === 0
        ? this.multiSelectFilterService.createUnfilteredSelectionState<TPlatform>()
        : this.multiSelectFilterService.createExplicitSelectionState(nextSelectedPlatformFeatures);

    this.selectionState.set(nextState);
    this.emitPlatformFeatures();
  }

  public onSelectAll(event: Event): void {
    event.stopPropagation();

    this.selectionState.set(this.multiSelectFilterService.createExplicitSelectionState(this.platformFeatures()));
    this.emitPlatformFeatures();
  }

  public onUnselectAll(event: Event): void {
    event.stopPropagation();

    this.selectionState.set(this.multiSelectFilterService.createUnfilteredSelectionState<TPlatform>());
    this.emitPlatformFeatures();
  }

  public onOpenedChange(opened: boolean): void {
    this.isPanelOpen = opened;

    if (opened) {
      this.panelSelectionSnapshot = this.getSelectionSnapshot();
    }
  }

  public onPanelClosed(): void {
    const nextSelectionSnapshot = this.getSelectionSnapshot();
    const selectionChanged =
      this.panelSelectionSnapshot !== null && this.panelSelectionSnapshot !== nextSelectionSnapshot;

    this.panelSelectionSnapshot = null;

    if (!selectionChanged) {
      return;
    }

    this.panelClosed.emit();
  }

  private emitPlatformFeatures(): Set<TPlatform> {
    const normalizedPlatformFeatures = this.multiSelectFilterService.normalizeValuesForEmission(
      this.selectionState(),
      this.platformFeatures(),
    );

    this.platformFeaturesChanged.emit(normalizedPlatformFeatures);

    return normalizedPlatformFeatures;
  }

  private getSelectionSnapshot(): string {
    return JSON.stringify(
      Array.from(
        this.multiSelectFilterService.normalizeValuesForEmission(this.selectionState(), this.platformFeatures()),
      ).sort(),
    );
  }

  private selectionStatesAreEqual(
    left: IProfileMultiSelectState<TPlatform>,
    right: IProfileMultiSelectState<TPlatform>,
  ): boolean {
    return (
      left.presentation === right.presentation &&
      left.uiSelectedValues.length === right.uiSelectedValues.length &&
      left.uiSelectedValues.every((value, index) => value === right.uiSelectedValues[index])
    );
  }
}
